/**
 * React Query hook that fetches the current user from `/api/v1/core/me`.
 *
 * - `user`: the SessionUser object, or null if not authenticated
 * - `isLoading`: initial fetch in flight
 * - `isError`: network or 5xx error (401 is a normal "not logged in" state)
 * - `isAuthenticated`: shortcut for `user != null`
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, type ApiError } from './api';
import { API_BASE } from './apiBase';

// The new API GET /v1/core/me returns the principal directly:
// { type, id, email, org_scope, permissions, super_admin }. The old Cockpit
// /me returned { user: { oid, name, groups, exp } }; the standalone-API
// cutover changed the contract, so we consume the principal shape here.
//
// `super_admin` (spec 00_access-binary-grants.md, 2026-08-09) is a flag from
// its own DB table, not a permission string: a super-admin bypasses every
// per-tool accessSlug and capability check (see toolVisibility.ts). It is NOT
// synthesized into `permissions` — every gate that needs to honor it takes an
// explicit `superAdmin` parameter and checks `user?.superAdmin` at the call
// site.
export type SessionUser = {
  type: string;
  id: string;
  email: string;
  name?: string;
  org_scope: string[];
  permissions: string[];
  superAdmin: boolean;
  // Back-compat alias. The gating helpers historically read `groups` (Entra
  // group OIDs). They now match accessSlug/capability strings, and we alias
  // `permissions` into `groups` so existing `user?.groups` call sites keep
  // working without a sweep. TODO: rename `groups` to `permissions` across the UI.
  groups: string[];
};

type Principal = {
  type: string;
  id: string;
  email: string;
  name?: string;
  org_scope?: string[];
  permissions?: string[];
  super_admin?: boolean;
};

export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const p = await apiGet<Principal>(`${API_BASE}/v1/core/me`);
    const permissions = p.permissions ?? [];
    return {
      type: p.type,
      id: p.id,
      email: p.email,
      name: p.name,
      org_scope: p.org_scope ?? [],
      permissions,
      superAdmin: p.super_admin ?? false,
      groups: permissions,
    };
  } catch (err) {
    const apiErr = err as ApiError;
    // 401: no valid session — redirect to login.
    // 403: authenticated but missing core:read — do NOT redirect (causes an
    //      infinite loop); let it propagate so the caller can show an
    //      access-denied page instead.
    if (apiErr.status === 401) {
      return null;
    }
    throw err;
  }
}

export type UseSessionResult = {
  user: SessionUser | null;
  isLoading: boolean;
  isError: boolean;
  isAuthenticated: boolean;
  /** True when /v1/core/me returned 403: user is authenticated but lacks core:read. */
  isForbidden: boolean;
  query: UseQueryResult<SessionUser | null, Error>;
};

export function useSession(): UseSessionResult {
  const query = useQuery<SessionUser | null, Error>({
    queryKey: ['session'],
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const user = query.data ?? null;
  const isForbidden = (query.error as ApiError)?.status === 403;
  return {
    user,
    isLoading: query.isLoading,
    isError: query.isError,
    isAuthenticated: user != null,
    isForbidden,
    query,
  };
}

export function getLoginUrl(returnTo?: string): string {
  const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ return: target });
  return `${API_BASE}/v1/core/auth/login?${params.toString()}`;
}

// Microsoft Entra login (HQ / @ampersandbrands.com staff). Alias of
// getLoginUrl, named so the login page reads clearly next to the Google path.
export function getEntraLoginUrl(returnTo?: string): string {
  return getLoginUrl(returnTo);
}

// Google Workspace login (Lolli & Pops store staff / @lolliandpops.com).
// Kicks off the OIDC flow on the API; `login_hint` pre-fills Google's account
// chooser with the email the user typed. If Google sign-in is not enabled yet,
// the API returns a 503 that the browser navigation surfaces as an error page,
// so the login page checks enablement before sending the user here.
export function getGoogleLoginUrl(email?: string, returnTo?: string): string {
  const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ return: target });
  if (email) params.set('login_hint', email);
  return `${API_BASE}/v1/core/auth/google/login?${params.toString()}`;
}

// The email-domain-to-provider routing rule, shared by the login page.
// @ampersandbrands.com -> Microsoft Entra; @lolliandpops.com -> Google.
export type LoginProvider = 'entra' | 'google' | 'unknown';

export function providerForEmail(email: string): LoginProvider {
  const at = email.lastIndexOf('@');
  if (at < 0) return 'unknown';
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (domain === 'ampersandbrands.com') return 'entra';
  if (domain === 'lolliandpops.com') return 'google';
  return 'unknown';
}

// `everywhere=true` triggers the full Microsoft sign-out (tenant-wide
// Entra end-session). Default is local-only logout: clears Cockpit's
// cookie but leaves Outlook / Teams / SharePoint sessions intact.
export function getLogoutUrl(everywhere = false): string {
  return everywhere
    ? `${API_BASE}/v1/core/auth/logout?everywhere=1`
    : `${API_BASE}/v1/core/auth/logout`;
}
